/**
 * SDMS Template Builder — a DEV report's View: the embedded DevExpress v20.1 Web Report Designer
 * (ADR-0014 host) on ONE SpreadSheetBuilder_Reporting row — the web twin of the Windows builder's
 * frmDynamicReporting. The API opens a designer session BOUND to that row; the designer shows the
 * template's Header/Detail maps as its field list (CHeader/CDetail when ReportSource =
 * Calibration) and saves the layout as XML into Layout_Xml (the legacy CodeDOM Layout is never
 * modified). Its Save writes straight away and is audited on the template — as in Windows.
 *
 * The iframe reports its dirty state over postMessage (Designer.cshtml host bridge), so closing
 * with unsaved layout changes asks first (standing rule 15).
 */
import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { LoaderSymbol } from '../../../components/loading';
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { designerFrameSrc, designerOrigin } from '../../reporting/reportDesignerUrl';
import { errorText, sdmsTemplateApi, type DesignerSession } from './sdmsTemplateApi';

export function SdmsReportDesignerDialog({ templateId, report, onClose }: {
  templateId: number;
  /** The DEV report to design; null = closed. */
  report: { uqID: number; name: string } | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [session, setSession] = useState<DesignerSession | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSession(null);
    setDirty(false);
    if (!report) return;
    let alive = true;
    void (async () => {
      try {
        const s = await sdmsTemplateApi.reportDesignerSession(templateId, report.uqID);
        if (alive) setSession(s);
      } catch (e) {
        notify.error(errorText(e, 'Could not open the report designer.'));
        onClose();
      }
    })();
    return () => { alive = false; };
    // A new session per report opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.uqID, templateId]);

  useEffect(() => {
    const origin = designerOrigin();
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== origin) return;
      const data = ev.data as { type?: string; dirty?: boolean } | null;
      if (data?.type === 'alpaca-rd:state') setDirty(!!data.dirty);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useUnsavedGuard(report != null && dirty);

  const src = useMemo(() => (session ? designerFrameSrc('design', session.report, session.token) : null), [session]);

  const close = async () => {
    if (dirty && !(await confirm({ title: 'Report Designer', message: 'Unsaved changes will be lost. Do you want to proceed?' }))) return;
    onClose();
  };

  return (
    <AppDialog open={report != null} title={`Report Designer — ${report?.name ?? ''}`} icon="ti-report" width={1400}
      onClose={() => void close()} submitOnEnter={false}
      footer={<>
        <span className="stb-hint">Save in the designer's menu stores the layout. It is kept as XML for Alpaca Intelligence; the legacy layout is not changed.</span>
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => void close()}>Close</button>
      </>}>
      <div className="stb-report-designer">
        {src
          ? <iframe title={`Report Designer — ${report?.name ?? ''}`} src={src} />
          : <div className="xl-loading"><LoaderSymbol /> Opening the report designer…</div>}
      </div>
    </AppDialog>
  );
}
